import React, { useEffect, createContext } from 'react';
import { makeStyles } from '@material-ui/core';
import { useImagesLoaded } from '../../hooks/useImagesLoaded';
import { useShareablesTemplateRoot } from '../../hooks/useShareablesTemplateRoot';
import { COLOR_BLACK } from '../colors';
import { skins } from './skins';
import lodashGet from 'lodash/get';
import { Black } from './Black';
import { White } from './White';
import { Magenta } from './Magenta';
import { SF_PRO_TEXT, LL_CIRCULAR_PRO } from '../fonts';
import { Yellow } from './Yellow';

const DEFAULT_SKIN = 'magenta';

const useStyles = makeStyles((theme)=> ({
    root: {
        fontFamily: ['"SFProText"', 'Arial', 'Helvetica', 'sans-serif'],
        color: COLOR_BLACK,
    },
    wrap: {
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        boxSizing: 'border-box',
        textAlign: 'center',
        fontSize: '34px',
        lineHeight: '40px',
    },
    watermark: {
        position: 'absolute',
        left: '50%',
        bottom: '30%',
        zIndex: 9999,

        '& img': {
            position: 'relative',
            left: '-50%',
        },
    }
}));

function SkinnedContent({ skin }) {
    switch (skin) {
        case 'black':
            return <Black />;
        case 'white':
            return <White />;
        case 'magenta':
            return <Magenta />;
        case 'yellow':
            return <Yellow />;
        default:
            return <Magenta />;
    }
}

export const TalkingPointsContext = createContext({});

const TalkingPointsContextProvider = ({ children, ...props })=> {
    return <TalkingPointsContext.Provider value={props}>{children}</TalkingPointsContext.Provider>;
};

const formatDate = (date)=> {
    const d = date ? new Date(`${date}T23:59:59.999Z`) : new Date();
    const options = {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    };
    return d.toLocaleString('en-US', options);
};

export default function TalkingPointsShareable(props) {
    const {
        rootClasses,
        parsedData,
        onImagesLoaded,
    } = useShareablesTemplateRoot(props);
    const classes = useStyles(props);
    const {
        images = [],
        user = {},
        skin = DEFAULT_SKIN,
        date = null,
        watermark = false,
    } = parsedData || {};
    const bgImage = lodashGet(skins, `${skin}.BACKGROUND_PATH`);
    const loadedBgs = useImagesLoaded([bgImage]);

    useEffect(() => {
        if(loadedBgs && !window.isLoaded) {
            if(typeof onImagesLoaded === 'function') {
                onImagesLoaded();
            } else {
                window.isLoaded = true;
            }
        }
    }, [loadedBgs, onImagesLoaded]);

    if(!parsedData) return null;

    return (
        <TalkingPointsContextProvider
            images={images}
            user={user}
            skin={skin}
            dateStr={formatDate(date)}
        >
            <link href={SF_PRO_TEXT} rel="stylesheet" />
            <link href={LL_CIRCULAR_PRO} rel="stylesheet" />

            <div className={`${rootClasses.root} ${classes.root}`} id="templateRoot">
                <div className={rootClasses.viewport}>

                    {/* Main Wrapper */}
                    <div className={classes.wrap}
                        style={{
                            backgroundImage: `url("${bgImage}")`,
                            color: lodashGet(skins, `${skin}.TEXT_COLOR`, COLOR_BLACK),
                        }}
                    >

                        <SkinnedContent skin={skin} />

                        {watermark && (<div className={classes.watermark}>
                            <img src="./images/watermark-stamp.svg" alt="Watermark" />
                        </div>)}
                    </div>
                </div>
            </div>
        </TalkingPointsContextProvider>
    );
}
